import * as React from "react"
import { Slot } from "@radix-ui/react-slot"
import { cva, type VariantProps } from "class-variance-authority"
import { cn } from "@/lib/utils"

const buttonVariants = cva(
  "inline-flex items-center justify-center whitespace-nowrap rounded-md text-sm font-medium transition-colors focus-visible:outline-none focus-visible:ring-1 focus-visible:ring-ring disabled:pointer-events-none disabled:opacity-50",
  {
    variants: {
      variant: {
        default: "bg-white/90 backdrop-blur-sm text-gray-900 shadow-[0_8px_16px_rgba(0,0,0,0.04)] hover:bg-white transition-all duration-200",
        destructive:
          "bg-red-600 text-white shadow-sm hover:bg-red-700 transition-all duration-200",
        /* Gray border, not white. The white one came from a design for buttons
           over a cover image; on a card or dialog — which is where `outline` is
           actually used — it vanished and the button became loose text. The
           backdrop-blur stays, so it still works over an image. */
        outline:
          "border border-gray-300 bg-white/90 backdrop-blur-sm shadow-[0_8px_16px_rgba(0,0,0,0.04)] hover:bg-gray-50 hover:border-gray-400 transition-all duration-200",
        /* `text-*-foreground` em vez de `text-white`: o padrão da variável é
           branco, então nada muda visualmente — mas quando a comunidade
           escolhe uma cor clara, o texto vira escuro em vez de sumir. */
        primary: "bg-primary-600 text-primary-foreground backdrop-blur-sm shadow-lg hover:bg-primary-700 transition-all duration-200",
        secondary: "bg-secondary-600 text-secondary-foreground backdrop-blur-sm shadow-lg hover:bg-secondary-700 transition-all duration-200",
        ghost: "hover:bg-white/80 backdrop-blur-sm transition-all duration-200",
        link: "text-primary-600 underline-offset-4 hover:underline hover:text-primary-700",
        create: "bg-primary-50 text-primary-700 backdrop-blur-sm shadow-sm hover:bg-primary-100 transition-all duration-200 font-medium",
      },
      size: {
        default: "h-[var(--density-field-h)] px-4 py-2",
        sm: "h-8 rounded-md px-3 text-xs",
        lg: "h-10 rounded-md px-8",
        icon: "h-9 w-9",
      },
    },
    defaultVariants: {
      variant: "default",
      size: "default",
    },
  }
)

interface ButtonProps
  extends React.ButtonHTMLAttributes<HTMLButtonElement>,
    VariantProps<typeof buttonVariants> {
  asChild?: boolean
}

const Button = React.forwardRef<HTMLButtonElement, ButtonProps>(
  ({ className, variant, size, asChild = false, ...props }, ref) => {
    const Comp = asChild ? Slot : "button"
    return (
      <Comp
        className={cn(buttonVariants({ variant, size, className }))}
        ref={ref}
        {...props}
      />
    )
  }
)
Button.displayName = "Button"

export { Button, buttonVariants }